import {
  Component,
  EventEmitter,
  Input,
  Output,
  ViewEncapsulation,
} from '@angular/core';

export interface TogglePillOption<T = string> {
  value: T;
  label: string;
  tooltip?: string;
  /** Analytics hook applied to this pill (Pendo targets these). */
  pendoClass?: string;
}

/**
 * Multi-select sibling of `app-segmented-tabs`: each pill toggles on its own.
 * Emitted values always follow `options` order, so consumers can compare or
 * serialise them as a stable string.
 */
@Component({
  encapsulation: ViewEncapsulation.None,
  selector: 'app-toggle-pill-group',
  templateUrl: './toggle-pill-group.component.html',
  styleUrls: ['./toggle-pill-group.component.scss'],
})
export class TogglePillGroupComponent<T = string> {
  @Input() options: TogglePillOption<T>[] = [];
  @Input() values: T[] = [];
  /** Deselecting below this count is ignored — e.g. 1 to keep at least one on. */
  @Input() minSelected = 0;
  @Input() disabled = false;
  @Input() idPrefix: string | null = null;
  @Output() valuesChange = new EventEmitter<T[]>();

  isActive(value: T): boolean {
    return this.values.includes(value);
  }

  pillId(value: T): string | null {
    return this.idPrefix ? `${this.idPrefix}-${String(value)}` : null;
  }

  toggle(value: T): void {
    if (this.disabled) return;
    const wasActive = this.isActive(value);
    if (wasActive && this.values.length <= this.minSelected) return;
    const next = this.options
      .map((option) => option.value)
      .filter((candidate) =>
        candidate === value ? !wasActive : this.isActive(candidate),
      );
    this.valuesChange.emit(next);
  }
}
